import * as t from 'io-ts';

import {makeApiSerializableType} from './apiTypes';
import {makeSafeArrayType} from './types/arrayTypes';
import {makeEnumRuntimeType} from './types/enumTypes';

/*
 * Runtime type.
 */

export enum TeammateBasicNotificationSettingsEnum {
  ALL = 'all',
  SMART = 'smart',
  NONE = 'none',
}
export enum TeammateBasicNotificationTypesEnum {
  APP = 'app',
  MOBILE = 'mobile',
  EMAIL = 'email',
}
export const RTeammateBasicNotificationTypesEnum = makeEnumRuntimeType(TeammateBasicNotificationTypesEnum);

export enum TeammateAdvancedNotificationNamesEnum {
  INBOUND_ANY_MESSAGE = 'inbound-any-message',
  OUTBOUND_ANY_MESSAGE = 'outbound-any-message',
  CONVERSATION_ASSIGNED = 'conversation-assigned',
  MENTION = 'mention',
  TRACKER_SHARE = 'tracker-share',
  REMINDER = 'reminder',
  POST_IN_ASSIGNED = 'post-in-assigned',
  POST_IN_FOLLOWED = 'post-in-followed',
  RECIPIENT_ERROR = 'recipient-error',
  RULE_NOTIFY = 'rule-notify',
}

export const RTeammateAdvancedNotificationNamesEnum = makeEnumRuntimeType(
  TeammateAdvancedNotificationNamesEnum,
);

const RBasicNotificationRule = t.record(
  RTeammateBasicNotificationTypesEnum,
  makeEnumRuntimeType(TeammateBasicNotificationSettingsEnum),
);

const RAdvancedNotificationRule = t.type({
  name: RTeammateAdvancedNotificationNamesEnum,
  isInAllInboxes: t.boolean,
  inboxIds: makeSafeArrayType(t.Integer),
  setting: t.record(RTeammateBasicNotificationTypesEnum, t.boolean),
});

export const RTeammateNotificationRules = t.type({
  basic: RBasicNotificationRule,
  advanced: makeSafeArrayType(RAdvancedNotificationRule),
});

/*
 * Type.
 */

export interface BasicNotificationRule extends t.TypeOf<typeof RBasicNotificationRule> {}
export interface AdvancedNotificationRule extends t.TypeOf<typeof RAdvancedNotificationRule> {}
export interface TeammateNotificationRules extends t.TypeOf<typeof RTeammateNotificationRules> {}

/*
 * IO methods.
 */

export const defaultBasicNotificationRules: BasicNotificationRule = {
  [TeammateBasicNotificationTypesEnum.APP]: TeammateBasicNotificationSettingsEnum.ALL,
  [TeammateBasicNotificationTypesEnum.MOBILE]: TeammateBasicNotificationSettingsEnum.ALL,
  [TeammateBasicNotificationTypesEnum.EMAIL]: TeammateBasicNotificationSettingsEnum.ALL,
};

export function parseTeammateNotificationRules(src: any): TeammateNotificationRules {
  return {
    basic: parseBasicNotificationRules(src),
    advanced: (Array.isArray(src?.rules) && src.rules.map(parseAdvancedNotificationRule)) || [],
  };
}

export function parseBasicNotificationRules(srcTeammate: any): BasicNotificationRule {
  return RBasicNotificationRule.is(srcTeammate?.basic) ? srcTeammate.basic : defaultBasicNotificationRules;
}

export function parseAdvancedNotificationRule(src: any): AdvancedNotificationRule {
  return {
    name: src.rule.notif_name,
    isInAllInboxes: src.inboxes?.allInboxes ?? false,
    inboxIds: src.inboxes?.ids ?? [],
    setting: {
      [TeammateBasicNotificationTypesEnum.APP]: src.rule.setting[0] ?? false,
      [TeammateBasicNotificationTypesEnum.MOBILE]: src.rule.setting[1] ?? false,
      [TeammateBasicNotificationTypesEnum.EMAIL]: src.rule.setting[2] ?? false,
    },
  };
}

export function serializeTeammateNotificationRules(src: TeammateNotificationRules) {
  return {
    basic: src.basic,
    rules: src.advanced.map((r) => serializeAdvancedNotificationRule(r)),
  };
}

function serializeAdvancedNotificationRule(src: AdvancedNotificationRule): any {
  return {
    inboxes: {
      allInboxes: src.isInAllInboxes,
      ids: src.inboxIds,
    },
    rule: {
      notif_name: src.name,
      setting: [src.setting.app, src.setting.mobile, src.setting.email],
    },
  };
}

/*
 * API type.
 */

export const ApiTeammateNotificationRules = makeApiSerializableType(
  'teammateNotificationRules',
  RTeammateNotificationRules,
  parseTeammateNotificationRules,
  serializeTeammateNotificationRules,
);
